import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  FormControl,
  ListItem,
  ListItemText,
  MenuItem,
  TextField,
  Box,
} from "@mui/material";
import DialogContentText from "@mui/material/DialogContentText";
import makeStyles from "@mui/styles/makeStyles";
import Button from "@mui/material/Button";
import { useSipServers } from "../security-settings/sip-servers/sipServerServices";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import { Skeleton } from "@mui/material";
import { isBlank, notBlank } from "../util/ext";
import Grid from "@mui/material/Grid";
import {
  allPass,
  append,
  equals,
  find,
  includes,
  mergeRight,
  not,
  propEq,
  reject,
} from "ramda";
import Typography from "@mui/material/Typography";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import {
  useAuthenticationCreate,
  useAuthenticationUpdate,
  useBarcodePrefixUpdate,
} from "./authenticationService";
import OrganizationAutoComplete from "../user-admin/OrganizationAutoComplete";

const useStyles = makeStyles(theme => ({
  errorContainer: {
    marginTop: theme.spacing(1),
    fontWeight: "bold",
    color: theme.palette.error.main,
  },

  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },

  selectEmpty: {
    marginTop: theme.spacing(2),
  },

  title: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },

  prefixList: {
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.primary.dark,
  },

  emptyList: {
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.primary.dark,
    fontSize: "1rem",
    fontStyle: "italic",
    color: theme.palette.primary.main,
  },
}));

const AddSipAuthenticationDialog = ({
  open,
  organizationId,
  authentication,
  onSave,
  onClose,
}) => {
  const classes = useStyles();
  const sipServers = useSipServers();
  const [errorMessage, setErrorMessage] = useState(null);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
  const [serverId, setServerId] = useState("");
  const [pinRequired, setPinRequired] = useState(false);
  const [blockJuvenile, setBlockJuvenile] = useState(false);
  const [showPrefixList, setShowPrefixList] = useState(false);
  const [prefix, setPrefix] = useState("");
  const [prefixList, setPrefixList] = useState([]);
  const [initialAuth, setInitialAuth] = useState(null);

  useEffect(() => {
    if (open && notBlank(authentication) && notBlank(sipServers.data)) {
      const server = find(
        allPass([
          propEq("hostname", authentication.hostname),
          propEq("port", authentication.port),
        ]),
        sipServers.data,
      );

      setInitialAuth({
        server,
        prefixes: authentication.prefixes,
        pinRequired: authentication.pinRequired,
        blockJuvenile: authentication.blockJuvenile,
      });

      setSelectedOrganizationId(authentication.organizationId);
      setServerId(server.sipServerId);
      setPinRequired(authentication.pinRequired);
      setBlockJuvenile(authentication.blockJuvenile);
      setPrefixList(authentication.prefixes || []);
      setShowPrefixList(true);
    } else {
      setSelectedOrganizationId(organizationId);
      setInitialAuth({});
      setServerId("");
      setPinRequired(false);
      setBlockJuvenile(false);
      setPrefixList([]);
      setShowPrefixList(false);
    }
  }, [open, authentication, organizationId, sipServers.data]);

  const handleServerChange = useCallback(event => {
    const value = event.target.value;
    setServerId(value);
    if (notBlank(value)) {
      setShowPrefixList(true);
    } else {
      setPrefixList([]);
      setShowPrefixList(false);
    }
  }, []);

  const handlePinRequiredChange = useCallback(event => {
    setPinRequired(event.target.checked);
  }, []);

  const handleBlockJuvenileChange = useCallback(event => {
    setBlockJuvenile(event.target.checked);
  }, []);

  const handlePrefixChange = useCallback(event => {
    setErrorMessage("");
    const value = event.target.value;
    setPrefix(value);
  }, []);

  const handlePrefixAdd = useCallback(() => {
    if (notBlank(prefix) && not(includes(prefix, prefixList))) {
      setPrefixList(prev => append(prefix, prev));
      setPrefix("");
      setErrorMessage("");
    } else {
      setErrorMessage("Invalid or exising prefix.");
    }
  }, [prefix, prefixList]);

  const handlePrefixRemove = useCallback(value => {
    setPrefixList(prev => reject(equals(value), prev));
  }, []);

  const handleOrganizationChange = orgId => {
    setSelectedOrganizationId(orgId);
  };

  const authenticationCreate = useAuthenticationCreate();
  const authenticationUpdate = useAuthenticationUpdate();
  const updateBarcodePrefixes = useBarcodePrefixUpdate();
  const handleApply = useCallback(async () => {
    if (notBlank(selectedOrganizationId)) {
      try {
        const body = {
          sipServerId: serverId,
          pinRequired: pinRequired,
          blockJuvenile: blockJuvenile,
        };

        if (isBlank(initialAuth)) {
          await authenticationCreate.mutateAsync({
            resourceIds: {
              organizationId: selectedOrganizationId,
              type: "sip",
            },
            data: body,
          });
        } else {
          console.log("Calling update...");
          if (
            not(equals(initialAuth.server.sipServerId, serverId)) ||
            not(equals(initialAuth.pinRequired, pinRequired)) ||
            not(equals(initialAuth.blockJuvenile, blockJuvenile))
          ) {
            await authenticationUpdate.mutateAsync({
              resourceIds: {
                organizationId: selectedOrganizationId,
                type: "sip",
              },
              data: body,
            });
          }
        }

        if (not(equals(initialAuth.prefixes, prefixList))) {
          await updateBarcodePrefixes.mutateAsync({
            resourceIds: {
              organizationId: selectedOrganizationId,
              type: "sip",
            },
            data: prefixList,
          });
        }

        onSave(mergeRight(body, { prefixes: prefixList }));
      } catch (error) {
        setErrorMessage(error);
      }
    } else {
      setErrorMessage("An Organization is Required.");
    }
  }, [
    selectedOrganizationId,
    serverId,
    pinRequired,
    blockJuvenile,
    prefixList,
    initialAuth,
    authenticationCreate,
    authenticationUpdate,
    updateBarcodePrefixes,
    onSave,
  ]);

  const handleCancel = useCallback(() => onClose(), [onClose]);

  return (
    <Dialog open={open} aria-labelledby="sip-dialog-title">
      <DialogTitle id="sip-dialog-title">
        {notBlank(initialAuth)
          ? "Edit SIP Authentication"
          : "Add New SIP Authentication"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Select a SIP Server for this Organization and add Optional Barcode
          Prefixes.
        </DialogContentText>
        {notBlank(errorMessage) && (
          <div className={classes.errorContainer}>{errorMessage}</div>
        )}

        {sipServers.isError && (
          <div className={classes.errorContainer}>{sipServers.error}</div>
        )}
        {sipServers.isLoading && <Skeleton variant="text" />}
        {sipServers.isSuccess && (
          <Box>
            <Grid alignItems="flex-end" container spacing={2}>
              <Grid item xs={12}>
                <OrganizationAutoComplete
                  autoFocus
                  disabled={notBlank(initialAuth)}
                  value={selectedOrganizationId}
                  label="Organization"
                  onChange={handleOrganizationChange}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <FormControl fullWidth={true} className={classes.formControl}>
                  <InputLabel shrink id="server-select-placeholder-label-label">
                    Server
                  </InputLabel>
                  <Select
                    labelId="server-select-placeholder-label-label"
                    id="server-select-placeholder-label"
                    value={serverId}
                    onChange={handleServerChange}
                    displayEmpty
                    className={classes.selectEmpty}
                  >
                    <MenuItem key="none" value="">
                      <em>None</em>
                    </MenuItem>
                    {sipServers.data.map(server => (
                      <MenuItem
                        key={server.sipServerId}
                        value={server.sipServerId}
                      >{`${server.hostname}:${server.port}`}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={12}>
                <FormControl fullWidth={true} className={classes.formControl}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={pinRequired}
                        onChange={handlePinRequiredChange}
                        name="pinRequired"
                        color="primary"
                      />
                    }
                    label="PIN Required"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={12}>
                <FormControl fullWidth={true} className={classes.formControl}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={blockJuvenile}
                        onChange={handleBlockJuvenileChange}
                        name="blockJuvenile"
                        color="primary"
                      />
                    }
                    label="Block Juvenile"
                  />
                </FormControl>
              </Grid>
            </Grid>
            {showPrefixList && (
              <Box>
                <Grid alignItems="flex-end" container spacing={2}>
                  <Grid item xs={12} md={5}>
                    <Typography variant="body1" className={classes.title}>
                      Barcode Prefixes
                    </Typography>
                    {notBlank(prefixList) ? (
                      <List dense classes={{ root: classes.prefixList }}>
                        {prefixList.map(prefix => (
                          <ListItem key={prefix}>
                            <ListItemText primary={prefix} />
                            <ListItemSecondaryAction>
                              <IconButton
                                onClick={() => handlePrefixRemove(prefix)}
                                edge="end"
                                aria-label="delete"
                                size="large"
                              >
                                <CloseIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Box className={classes.emptyList}>
                        Add a Barcode Prefix...
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={8} md={5}>
                    <TextField
                      autoFocus
                      margin="dense"
                      id="prefix"
                      label="Prefix"
                      type="text"
                      value={prefix}
                      onChange={handlePrefixChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={4} md={2}>
                    <div>
                      <Button
                        onClick={handlePrefixAdd}
                        variant="text"
                        color="primary"
                      >
                        Add
                      </Button>
                    </div>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddSipAuthenticationDialog;
